import { useEffect, useRef, useState } from 'react';

declare const __BUILD_VERSION__: string | undefined;

const CHECK_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Polls /version.json in the background and sets `updateAvailable`
 * to true when the deployed build version differs from the running one.
 *
 * __BUILD_VERSION__ is injected by Vite at build time.
 */
export default function useVersionCheck(): {
  updateAvailable: boolean;
  reload: () => void;
} {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // __BUILD_VERSION__ is undefined during dev (vite dev server).
    if (typeof __BUILD_VERSION__ === 'undefined') return;

    const currentVersion = __BUILD_VERSION__;

    async function check() {
      try {
        const res = await fetch('/version.json', { cache: 'no-cache' });
        if (!res.ok) return;
        const { version } = (await res.json()) as { version?: string };
        if (version && version !== currentVersion) {
          setUpdateAvailable(true);
        }
      } catch {
        // Network error — ignore, will retry next interval.
      }
    }

    // First check after a short delay so we don't slow initial load.
    const initialTimeout = setTimeout(check, 10_000);
    timerRef.current = setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimeout);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  function reload() {
    window.location.reload();
  }

  return { updateAvailable, reload };
}
